import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './VerticalNavbar.css';
import { FaLink, FaMoon, FaRegFolder, FaSun } from 'react-icons/fa';
import { BiHistory, BiSolidHelpCircle, BiBrain, FaSolidTeam } from 'react-icons/bi';
import { HiSparkles } from 'react-icons/hi';
import { IoAdd, IoCalendarClearOutline, IoShareSocialSharp } from 'react-icons/io5';
import {
  MdArrowBackIosNew,
  MdArrowForwardIos,
} from 'react-icons/md';
import { TbFileInvoice, TbLogout, TbTemplate } from 'react-icons/tb';
import { motion, AnimatePresence } from 'framer-motion';
import useLocalStorage from 'use-local-storage';
import { useWorkspace } from '../../../context/WorkspaceContext';
import DropdownButton from '../../Tools/DropdownButton';
import { GoTools } from 'react-icons/go';
import { getAuth, signOut } from 'firebase/auth';
import { IoMdHome, IoMdSettings } from 'react-icons/io';
import { HiOutlineUpload } from 'react-icons/hi';
import { RiTeamFill, RiUserShared2Line } from 'react-icons/ri';
import { FiBarChart2 } from 'react-icons/fi';
import WorkspaceSelector from '../../../components/WorkspaceSelector/WorkspaceSelector';

const VerticalNavbar = () => {
  const [isExpanded, setIsExpanded] = useLocalStorage('navbarIsExpanded', true);
  const [toolsOpen, setToolsOpen] = useState(true); // <-- STATE FOR TOOLS SECTION
  const [overviewOpen, setOverviewOpen] = useState(true); // <-- STATE FOR OVERVIEW SECTION
  const [contentOpen, setContentOpen] = useState(true); // <-- STATE FOR CONTENT SECTION

  const menuRef = useRef(null);
  const navigate = useNavigate();
  const [theme, setTheme] = useLocalStorage('theme', 'light');
  const location = useLocation();
  const [currentPath, setCurrentPath] = useState(null);
  const hasRunOnceRef = useRef(false);
  const { firestoreUser } = useWorkspace();

  useEffect(() => {
    if (!hasRunOnceRef.current && location.pathname !== '') {
      setCurrentPath(location.pathname);
    }
  }, [location]);

  useEffect(() => {
    localStorage.setItem('navbarIsExpanded', JSON.stringify(isExpanded));
  }, [isExpanded]);

  const toggleNavbar = () => {
    setIsExpanded((prev) => !prev);
  };

  useEffect(() => {
    const storedIsExpanded = localStorage.getItem('navbarIsExpanded');
    if (storedIsExpanded === null) {
      const handleResize = () => {
        const screenWidth = window.innerWidth;
        setIsExpanded(screenWidth > 768);
      };

      handleResize();
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [setIsExpanded]);

  const isActive = (currentPath, path) => {
    return currentPath === path ? 'active' : '';
  };


  const sidebarVariants = {
    expanded: {
      width: 230,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 20,
      },
    },
    collapsed: {
      width: 80,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 20,
      },
    },
  };

  const handleSignOut = () => {
    const auth = getAuth();
    signOut(auth)
      .then(() => navigate('/login'))
      .catch((error) => console.error(error));
  };

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  // Standardized icon size for consistency
  const iconSize = isExpanded ? 20 : 24;

  // Reusable collapsible section component (optional navigateTo: clicking header navigates instead of toggling; headerRightShowOnHover: show headerRight only on header hover)
  const CollapsibleSection = ({ title, isOpen, setIsOpen, children, defaultOpen = true, headerRight, navigateTo, headerRightShowOnHover }) => (
    <div className={`flex flex-col ${isExpanded ? 'mt-4' : 'mt-3'}`}>
      {isExpanded && (
        <div
          onClick={() => (navigateTo ? navigate(navigateTo) : setIsOpen(!isOpen))}
          className={`flex items-center justify-between cursor-pointer hover:bg-gray-700 p-1 rounded-md mb-1 ${headerRightShowOnHover ? 'group' : ''}`}
        >
          <p className="text-[11px] opacity-70 font-semibold nav-link uppercase tracking-wider">
            {title}
          </p>
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            {headerRightShowOnHover ? (
              <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                {headerRight}
              </span>
            ) : (
              headerRight
            )}
            <MdArrowForwardIos
              className={`h-3 w-3 text-gray-400 transform transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
            />
          </div>
        </div>
      )}
      <AnimatePresence mode="wait">
        {(isOpen || !isExpanded) && (
          <motion.div
            key={`${title}-${isOpen}`}
            initial={false}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ 
              duration: 0.15,
              ease: "easeInOut",
              height: { duration: 0.15 },
              opacity: { duration: 0.1 }
            }}
            className="overflow-hidden"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  // Reusable navigation item component
  const NavItem = ({ to, icon: Icon, label, isSubmenu = false }) => (
    <div
      className={`flex items-center ${isExpanded ? '' : 'justify-center'} group relative hover:bg-gray-700 ${isSubmenu ? 'p-0.5 mb-1 ml-4' : 'p-1 mb-1'} rounded-md ${isActive(currentPath, to)}`}
    >
      <Link
        to={to}
        className={`flex items-center gap-2 text-[${isSubmenu ? '13px' : '14px'}] w-full ${!isExpanded ? 'justify-center' : ''}`}
      >
        <Icon
          size={isSubmenu ? iconSize - 2 : iconSize}
          color="white"
          className="navbar-icon"
        />
        {isExpanded && (
          <span className="nav-link">
            {label}
          </span>
        )}
      </Link>
    </div>
  );

  return (
    <motion.div
      layout
      className={`vertical-menu shadow-lg ${isExpanded ? 'min-w-[200px] max-w-[200px] p-[16px]' : 'w-[80px] px-2 py-3'
        } bg-gray-200 fixed top-0 left-0 h-screen flex flex-col`}
      variants={sidebarVariants}
      animate={isExpanded ? 'expanded' : 'collapsed'}
      initial={false}
      transition={{ layout: { duration: 0.2, ease: "easeInOut" } }}
    >
      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto no-scrollbar pr-1">
      {/* LOGO */}
      <div className={`relative ${isExpanded ? 'py-4' : 'pt-[16px] pb-4'} flex justify-center items-center`}>
        <Link to="/dashboard" className="flex items-center justify-center relative overflow-hidden">
          <AnimatePresence mode="wait">
            {isExpanded ? (
              <motion.img
                key="expanded-logo"
                src={`${process.env.PUBLIC_URL}/logo/logo-horizontal-white.png`}
                alt="Logo"
                className="max-h-[100px]"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              />
            ) : (
              <motion.img
                key="collapsed-logo"
                src={`${process.env.PUBLIC_URL}/logo/favicon-nobg.png`}
                alt="Logo"
                className="max-w-[60px] max-h-[60px]"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.3 }}
              />
            )}
          </AnimatePresence>
        </Link>
      </div>

      {/* TOGGLE BUTTON */}
      <motion.div
        layout
        className={`px-2 py-1 text-white absolute right-0 top-16`}
        onClick={toggleNavbar}
      >
        {isExpanded ? (
          <MdArrowBackIosNew
            size={24}
            color="white"
            className="opacity-50 hover:border-gray-300 border-2 border-transparent rounded-xl p-1 cursor-pointer"
          />
        ) : (
          <MdArrowForwardIos
            size={24}
            color="white"
            className="opacity-50 hover:border-gray-300 border-2 border-transparent rounded-xl p-1 cursor-pointer"
          />
        )}
      </motion.div>

      {/* WORKSPACE SELECTOR */}
      <WorkspaceSelector isExpanded={isExpanded} variant="desktop" />

      {/* OVERVIEW MENU */}
      <CollapsibleSection title="OVERVIEW" isOpen={overviewOpen} setIsOpen={setOverviewOpen}>
        <NavItem to="/dashboard" icon={IoMdHome} label="Dashboard" />
        <NavItem to="/dashboard/analytics" icon={FiBarChart2} label="Analytics" />
        <NavItem to="/dashboard/library" icon={FaRegFolder} label="Library" />
      </CollapsibleSection>

      {/* CONTENT MENU */}
      <CollapsibleSection title="CONTENT" isOpen={contentOpen} setIsOpen={setContentOpen}>
        <Link
          to="/dashboard/upload"
          className={`flex items-center gap-2 w-full mb-1 p-1 rounded-md transition-all duration-200 group ${isActive(currentPath, '/dashboard/upload') ? 'bg-blue-600/20 border-blue-500/30' : 'bg-blue-500/10 hover:bg-blue-500/20'} ${!isExpanded ? 'justify-center' : ''}`}
        >
          <div className={`flex items-center gap-2 ${isExpanded ? 'w-full' : 'justify-center'}`}>
            <span className={`flex items-center justify-center transition-colors ${isActive(currentPath, '/dashboard/upload') ? 'text-blue-400' : 'text-blue-500 group-hover:text-blue-400'}`}>
              <IoAdd size={22} />
            </span>
            {isExpanded && (
              <span className={`nav-link text-[14px] font-semibold tracking-tight ${isActive(currentPath, '/dashboard/upload') ? 'text-blue-300' : 'text-blue-400 group-hover:text-blue-300'}`}>
                Upload
              </span>
            )}
          </div>
        </Link>
        <NavItem to="/dashboard/schedule" icon={IoCalendarClearOutline} label="Calendar" />
        <NavItem to="/dashboard/posts" icon={BiHistory} label="Posts" />
      </CollapsibleSection>

      {/* TOOLS MENU */}
      <CollapsibleSection
        title="TOOLS"
        isOpen={toolsOpen}
        setIsOpen={setToolsOpen}
        navigateTo="/dashboard/tools"
        headerRightShowOnHover
        headerRight={
          <Link
            to="/dashboard/tools"
            className="text-[9px] opacity-70 nav-link hover:opacity-100 whitespace-nowrap"
          >
            More...
          </Link>
        }
      >
        <NavItem to="/dashboard/tools/ai-generator" icon={BiBrain} label="AI Generator" />
        <NavItem to="/dashboard/tools/image-editor" icon={HiSparkles} label="Image Editor" />
        <NavItem to="/dashboard/tools/link-in-bio" icon={FaLink} label="Link In Bio" />
        <NavItem to="/dashboard/tools/templates" icon={TbTemplate} label="Templates" />
      </CollapsibleSection>

      </div>

      {/* Bottom section with CONFIG and USER PROFILE */}
      <div className="mt-auto">
        {/* CONFIG MENU */}
        <div className="flex flex-col">
          {isExpanded && (
            <p className="text-[11px] opacity-70 font-semibold nav-link mb-1 uppercase tracking-wider">
              CONFIG
            </p>
          )}
          <NavItem to="/dashboard/socials" icon={IoShareSocialSharp} label="Socials" />
          <NavItem to="/dashboard/support" icon={BiSolidHelpCircle} label="Support" />
          <NavItem to="/dashboard/affiliate" icon={TbTemplate} label="Affiliate" />
        </div>

      {/* USER PROFILE WITH MENU */}
      <div ref={menuRef} className="border-t border-gray-500 mt-4 relative flex justify-center p-2">
        <DropdownButton
          left={100}
          label={
            <div
              className="w-full flex justify-between items-center bg-transparent border-none cursor-pointer hover:bg-gray-700 p-1 rounded-md"
            >
              <div className="flex items-center gap-2 w-full justify-between">
                {/* User avatar */}
                <div
                  className={`bg-green-100 text-green-600 flex items-center justify-center rounded-full ${!isExpanded ? 'h-10 w-10' : 'h-8 w-8 '
                    } ${isActive(currentPath, '/dashboard/settings') ? 'bg-blue-100 text-blue-600' : ''}`}
                >
                  {firestoreUser.username.charAt(0)}
                </div>
                {/* Username and Email */}
                {isExpanded && (
                  <div className="text-left">
                    <div className="nav-link font-medium">{firestoreUser.username}</div>
                    <div className="text-xs nav-link whitespace-nowrap overflow-hidden text-ellipsis max-w-[120px]">
                      {firestoreUser.email}
                    </div>
                  </div>
                )}
              </div>
              {isExpanded && (
                <MdArrowForwardIos className="h-4 w-4 text-gray-400 group-hover:text-gray-200" />
              )}
            </div>
          }
          options={[
            {
              heading: (
                <div className="flex justify-between items-center gap-2">
                  <div className="text-sm font-semibold bg-green-100 text-green-600 flex items-center justify-center rounded-md px-3 py-2">
                    {firestoreUser.username.charAt(0)}
                  </div>
                  <div className="text-xs text-gray-500 flex flex-col">
                    <div className="text-sm font-semibold">
                      {firestoreUser.username}
                    </div>
                    <div className="text-xs text-gray-500">
                      {firestoreUser.email}
                    </div>
                  </div>
                </div>
              ),
            },
            {
              label: theme === 'light' ? 'Light mode' : 'Dark mode',
              onClick: () => toggleTheme(),
              icon:
                theme === 'light' ? (
                  <FaSun className="text-yellow-600 font-bold" />
                ) : (
                  <FaMoon className="text-blue-600 font-bold" />
                ),
            },
            {
              label: 'Settings',
              onClick: () => navigate('/dashboard/settings'),
              icon: <IoMdSettings className="text-blue-600 font-bold" />,
            },
            {
              label: 'Subscription',
              onClick: () => navigate('/dashboard/settings/subscription'),
              icon: <TbFileInvoice className="text-blue-600 font-bold" />,
            },
            {
              label: 'Workspaces',
              onClick: () => navigate('/dashboard/settings/team'),
              icon: <RiTeamFill  className="text-blue-600 font-bold" />,
            },
            {
              label: 'Affiliate Marketing',
              onClick: () => navigate('/dashboard/affiliate'),
              icon: <RiUserShared2Line className="text-blue-600 font-bold" />,
            },
            {
              label: 'Logout',
              onClick: () => handleSignOut(),
              color: 'red',
              icon: <TbLogout size={20} color="red" />,
            },
          ]}
        />
      </div>
      </div>
    </motion.div >
  );
};

export default VerticalNavbar;
